import type { TFunction } from "i18next";
import { adsAuthHeaders } from "@/lib/ads";
import { uploadToWebStorage } from "@/lib/web-upload";
import { FACELOCK_MAX_EDGE, isAcceptedPhoto, preparePhotoForUpload } from "@/lib/photo-prep";
import { MARK_SECTIONS, type MarkSection } from "@/shared/face-qc/marks.mjs";

// FaceLock avatars ("casting crew") — the frontend's ONLY knowledge of
// /api/web/facelock/* (plan B6). Every shape the server sends or receives is
// declared here and nowhere else, so aligning with the backend is one file.
//
//   GET    /avatars              -> FaceLockCrew (404 {error:"feature_off"} when disabled)
//   GET    /avatars/:id          -> FaceLockAvatar (or {avatar})
//   POST   /avatars              {name, photos (16-24: one complete set), idempotency_key} -> 202
//                                 free for the account's first avatar, 50 credits after, whatever the count
//   PATCH  /avatars/:id          {name}
//   POST   /avatars/:id/picture  {} "Draw its picture again": free, the owner (or an admin)
//                                 -> 202 {avatar, queued, job_id} | 200 {avatar, queued:false}
//                                 {fresh:true}: "Draw new pictures to choose from" (a picture with < 2 choices)
//   POST   /avatars/:id/picture/choose {index} "Choose its picture": free, the owner (or an admin)
//                                 -> {avatar} | 400 picture_unknown
//   POST   /admin/avatars/:id/adopt {photos} admin only, free   -> 202 {avatar, avatar_id, job_id}
//                                 a legacy avatar takes 3-24 photos, any other 8-24
//   DELETE /avatars/:id/photos    the owner (or an admin)       -> {avatar, deleted, pending}
//                                 every photo file of it, deleted (FaceLock Scan's "you can ask
//                                 us to delete them"); it keeps its picture but can't be cast
//                                 until it has photos again (photos_deleted, message photos_deleted)
//
//   POST   /avatars/:id/enhance   ENHANCEMENTS (FaceLock Ink Scan, FaceShot SharpShoot):
//                                 {kinds, marks, parts, outfit, photos, idempotency_key} -> 202
//   GET    /avatars/:id/enhance   the side-by-side: the avatar now and the enhanced one, shot by shot
//   POST   /avatars/:id/enhance/:eid/decide {keep}   keep it (the avatar switches) or not (nothing changes)
//   POST   /avatars/:id/enhance/rollback              "Go back to the previous version"
//                                 Improvement only: nothing about the avatar changes until a person
//                                 keeps the result, and it is charged only then.
//
// The server never sends a weights slug; the page only ever names an avatar by id.
//
// There is no strengthen and no lock (owner decision, 2026-09-30): the FaceLock
// Scan captures the identity completely. An older server's "locked" reads as
// "ready", and nothing here offers a strengthen.
//
// THE PICTURE (owner rule, 2026-09-30): an avatar's picture_url is ALWAYS a
// portrait drawn with the avatar itself, never an uploaded photo. While it has
// none, picture_state says why and the page shows a neutral silhouette that
// says so ("Drawing its picture…"), never a letter and never a photo.
//
// PEOPLE JUDGE LIKENESS (owner rule, 2026-09-30, final): the server never
// sends a likeness score. Every picture it drew is a choice (picture_choices,
// `broken` being the only thing the score may say: no face, or clearly
// somebody else), and its owner picks the one the card shows.

export const FACELOCK_API = "/api/web/facelock";
/** One complete set (owner decision 2026-09-30): 16 to 24 photos (the scan sends 24). */
export const FACELOCK_MIN_PHOTOS = 16;
export const FACELOCK_MAX_PHOTOS = 24;
export const FACELOCK_NAME_MAX = 40;
/** Add photos (admin): the fewest a legacy avatar takes (FACELOCK_CREW_LEGACY_MIN_PHOTOS);
 *  any other avatar takes FACELOCK_ADOPT_MIN_PHOTOS (FACELOCK_CREW_MIN_KEPT). */
export const FACELOCK_ADOPT_LEGACY_MIN_PHOTOS = 3;
export const FACELOCK_ADOPT_MIN_PHOTOS = 8;
/** GET /avatars again this often while any FaceLock is training. */
export const FACELOCK_POLL_MS = 15000;

export type FaceLockStatus = "checking" | "training" | "finishing" | "ready" | "failed";

/** Where an avatar's picture stands (the server's view.picture_state). */
export type FaceLockPictureState =
  /** picture_url is its portrait, drawn with the avatar itself */
  | "ready"
  /** its Flux stills file is being trained first ("Learning the face for pictures…") */
  | "learning"
  /** a portrait is being drawn ("Drawing its picture…") */
  | "drawing"
  /** none could be drawn: the card offers "Draw its picture again" */
  | "failed"
  /** it is being built; the picture comes at the end */
  | "waiting"
  /** nothing to draw it from yet (no photos on file, or a build that failed) */
  | "none";

export interface FaceLockEligibility {
  signed_up: boolean;
  purchased_credits: number;
  required_purchased: number;
  can_create: boolean;
  /** Credits the NEXT avatar costs, whatever the photo count: 0 for the first, then 50. */
  next_cost: number;
  /** One complete set: the fewest and the most photos a create takes (16 and 24). */
  photo_min: number;
  photo_max: number;
  /** Optional: an admin account is not held to the purchase threshold. */
  gate_waived?: boolean;
  /** Optional: a FaceLock of this account is being built right now. */
  busy?: boolean;
  /** The most scan pictures past the set a create may carry, labelled (ScanPhotoLabel); 0 or absent: plain links only. */
  photo_extra_max?: number;
  /** A paid kids storybook unlocked the free avatar. */
  book_buyer?: boolean;
  /** The free avatar is unlocked (credits bought, a storybook bought, or waived). Absent on an older server. */
  unlocked?: boolean;
  /** The account's credit balance now. */
  balance?: number;
  /** Not unlocked: the flat-fee avatar to pay for (a pack whose credits the create then charges). */
  flat_fee?: FaceLockFlatFee | null;
}

/** The flat-fee avatar: pay `price_cents` (MXN) / `usd_cents` once, get `credits`, the avatar's price. */
export interface FaceLockFlatFee {
  pack_type: string;
  credits: number;
  price_cents: number;
  usd_cents: number | null;
}

/** What a scan picture is, sent with its link when the server takes labelled pictures (photo_extra_max > 0). */
export interface ScanPhotoLabel {
  /** The checklist item it was kept for. */
  bucket: string;
  /** The scan part it was taken in. */
  kind: "face" | "upper" | "full";
  w: number;
  h: number;
  bytes: number;
}

/** One picture the avatar's draw kept ("Choose its picture"). Never a score. */
export interface FaceLockPictureChoice {
  /** The id the server knows it by (POST /avatars/:id/picture/choose {index}). */
  index: number;
  url: string;
  /** The alarm only: no face, or clearly somebody else. It can still be picked. */
  broken: boolean;
  /** The one the card shows now. */
  chosen: boolean;
}

/** An enhancement: FaceLock Ink Scan (tattoos and marks) and/or FaceShot SharpShoot (another moment). */
export type EnhanceKind = "ink" | "sharpshoot";
export type MarkKind = "tattoo" | "scar" | "mole" | "birthmark";
export type EnhanceState = "checking" | "training" | "drawing" | "ready" | "accepted" | "rejected" | "failed" | "expired" | "rolled_back";

/** A tattoo, scar, mole or birthmark the avatar carries (or an Ink Scan names). `place`: marks.mjs MARK_PLACE_LIST. */
export interface FaceLockMark {
  place: string;
  kind: MarkKind;
  /** What it is, in the person's words ("a small cross"). */
  what: string;
  /** Shows in most outfits: named in every picture of the avatar. */
  always: boolean;
}

/** The card's enhancement block (the server's view.enhance). Never a slug or a score. */
export interface FaceLockEnhanceView {
  /** This account may enhance avatars at all. */
  available: boolean;
  state: EnhanceState | null;
  kinds: EnhanceKind[];
  /** How the last one ended (faceLock.enhance.outcome.*), when it is over. */
  outcome: string | null;
  can_start: boolean;
  /** Being learned or drawn now (the page polls). */
  running: boolean;
  /** The side-by-side is ready for a decision. */
  can_decide: boolean;
  /** "Go back to the previous version" is offered. */
  can_rollback: boolean;
  marks: FaceLockMark[];
  /** Credits held at the start and charged only when the result is kept (0: free). */
  price: number;
  /** Who the avatar shows: the scan's captions say "his" or "her". */
  subject: "man" | "woman";
}

/** Where an avatar's ANIMATED picture stands (the server's view.animated_picture.state). */
export type FaceLockAnimatedState = "ready" | "drawing" | "failed" | "waiting" | "none";

/**
 * The same portrait in the 3D animated look (services/facelock-crew/picture-3d.js),
 * shown beside the realism picture. Drawn with the avatar itself, never a photo.
 */
export interface FaceLockAnimatedPicture {
  url: string | null;
  state: FaceLockAnimatedState;
  /** "Draw it again" (failed) or "Draw a new one" (ready): new seeds. */
  can_redraw: boolean;
  /** What that press costs: 0 for "Draw it again" after a failed draw, the price for a new version of one it has. */
  redraw_credits: number;
  /** Every version the draw kept ("Choose its animated picture"); [] while it has none. */
  choices: Array<{ index: number; url: string; broken: boolean }>;
  /** The version the card shows. */
  chosen_index: number | null;
}

export interface FaceLockAvatar {
  id: string;
  name: string;
  /** A portrait drawn with the avatar itself; null while it has none (never an uploaded photo). */
  picture_url: string | null;
  /** Where the picture stands; missing on an older server (read as "ready" with a picture, else "none"). */
  picture_state?: FaceLockPictureState;
  /** Every picture its draw kept, for "Choose its picture" (none for a picture drawn before they were kept). */
  picture_choices?: FaceLockPictureChoice[];
  /** A picture from before its choices were kept: "Draw new pictures to choose from" is offered. */
  can_draw_choices?: boolean;
  status: FaceLockStatus;
  /** Usable in a corrido video or an ad right now. */
  castable: boolean;
  photo_count: number;
  /** A faceLock.messages.* key explaining the latest outcome, when there is one. */
  message_key: string | null;
  /** 0-100 while training, null otherwise. */
  progress: number | null;
  created_at: string;
  /** Trained, but no photos on file yet (a legacy avatar before an admin adds them): not usable. */
  needs_photos?: boolean;
  /** Its face photos passed the check: a kids storybook (drawn from photos alone) can use it, even while it trains. */
  photos_ready?: boolean;
  /** An admin's Add photos is running on it (it stays as it was until that finishes). */
  adopting?: boolean;
  /** Made before the casting crew (backfilled from the legacy registry). */
  legacy?: boolean;
  /** Its photos were deleted on request: it keeps its picture but can't be cast. */
  photos_deleted?: boolean;
  /** It still has photo files on file, and nothing runs on it: "Delete its photos" is offered. */
  can_delete_photos?: boolean;
  /** STILLS FIRST (lib/facelock-stills.ts): pictures (a corrido storyboard) work now. */
  stills_ready?: boolean;
  /** Videos work too (the same as `castable`). */
  video_ready?: boolean;
  /** About how long its video files still take, in seconds; null when nothing is pending. */
  video_eta_s?: number | null;
  /** ENHANCEMENTS: only when the server says (GET /avatars). */
  enhance?: FaceLockEnhanceView;
  /** The animated version of its picture; absent while the server has it switched off. */
  animated_picture?: FaceLockAnimatedPicture;
}

export interface FaceLockCrew {
  enabled: boolean;
  /** Optional: false when the corrido/ads picker is switched off (FACELOCK_CREW_PICKER=0). */
  picker?: boolean;
  /** FACELOCK_CREW_REQUIRED: a music video with the artist in it, or an ad with a
   *  person in it, is made with a FaceLock or not at all (no photo upload). */
  required?: boolean;
  /** An admin account: the page offers the admin-only Add photos. */
  admin?: boolean;
  eligibility: FaceLockEligibility;
  avatars: FaceLockAvatar[];
}

export type FaceLockLoad =
  | { state: "on"; crew: FaceLockCrew }
  /** Disabled (404 feature_off, enabled:false, signed out): render nothing. */
  | { state: "off" }
  /** A transient failure: keep whatever was on screen. */
  | { state: "error"; status: number };

export type FaceLockResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string; message: string };

const STATUSES: FaceLockStatus[] = ["checking", "training", "finishing", "ready", "failed"];
const PICTURE_STATES: FaceLockPictureState[] = ["ready", "learning", "drawing", "failed", "waiting", "none"];
const ANIMATED_STATES: FaceLockAnimatedState[] = ["ready", "drawing", "failed", "waiting", "none"];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}
const num = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);

function normalizeChoices(raw: unknown): FaceLockPictureChoice[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((c): FaceLockPictureChoice[] => {
    if (!isRecord(c) || typeof c.url !== "string" || !c.url || typeof c.index !== "number" || !Number.isInteger(c.index)) return [];
    return [{ index: c.index, url: c.url, broken: c.broken === true, chosen: c.chosen === true }];
  });
}

const ENHANCE_STATES: EnhanceState[] = ["checking", "training", "drawing", "ready", "accepted", "rejected", "failed", "expired", "rolled_back"];
const MARK_KINDS: MarkKind[] = ["tattoo", "scar", "mole", "birthmark"];

function normalizeMarks(raw: unknown): FaceLockMark[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((m): FaceLockMark[] => (isRecord(m) && typeof m.place === "string" && MARK_KINDS.includes(m.kind as MarkKind)
    ? [{ place: m.place, kind: m.kind as MarkKind, what: str(m.what), always: m.always === true }] : []));
}

export function normalizeEnhance(raw: unknown): FaceLockEnhanceView | null {
  if (!isRecord(raw)) return null;
  return {
    available: raw.available === true,
    state: ENHANCE_STATES.includes(raw.state as EnhanceState) ? (raw.state as EnhanceState) : null,
    kinds: (Array.isArray(raw.kinds) ? raw.kinds : []).filter((k): k is EnhanceKind => k === "ink" || k === "sharpshoot"),
    outcome: typeof raw.outcome === "string" && raw.outcome ? raw.outcome : null,
    can_start: raw.can_start === true,
    running: raw.running === true,
    can_decide: raw.can_decide === true,
    can_rollback: raw.can_rollback === true,
    marks: normalizeMarks(raw.marks),
    price: Math.max(0, num(raw.price, 0)),
    subject: raw.subject === "woman" ? "woman" : "man",
  };
}

/** The animated picture, defensively: a url only ever means "ready". Null: switched off (or an older server). */
export function normalizeAnimated(raw: unknown): FaceLockAnimatedPicture | null {
  if (!isRecord(raw)) return null;
  const url = typeof raw.url === "string" && raw.url ? raw.url : null;
  const sent = ANIMATED_STATES.includes(raw.state as FaceLockAnimatedState) ? (raw.state as FaceLockAnimatedState) : "none";
  const credits = typeof raw.redraw_credits === "number" && Number.isFinite(raw.redraw_credits) && raw.redraw_credits > 0
    ? Math.round(raw.redraw_credits) : 0;
  const choices = Array.isArray(raw.choices) ? raw.choices.filter((c): c is { index: number; url: string; broken?: boolean } =>
    isRecord(c) && typeof c.url === "string" && !!c.url && Number.isInteger(c.index))
    .map((c) => ({ index: c.index, url: c.url, broken: c.broken === true })) : [];
  // A new version being drawn over one it has: the card keeps the picture and says so (owner, 10-11:
  // "I just tried to redraw ... and it just loaded the same image").
  const state: FaceLockAnimatedState = url ? (sent === "drawing" ? "drawing" : "ready") : (sent === "ready" ? "none" : sent);
  return { url, state, can_redraw: raw.can_redraw === true, redraw_credits: credits, choices,
    chosen_index: typeof raw.chosen_index === "number" && Number.isInteger(raw.chosen_index) ? raw.chosen_index : null };
}

/** One avatar row, defensively: a missing field never reaches the UI as undefined. */
export function normalizeAvatar(raw: unknown): FaceLockAvatar | null {
  const r = isRecord(raw) && isRecord(raw.avatar) ? raw.avatar : raw;
  if (!isRecord(r)) return null;
  const id = str(r.id) || str(r._id) || str(r.avatar_id);
  if (!id) return null;
  // An older server's "locked" (strengthen is gone): the ready avatar it is.
  const status = r.status === "locked" ? "ready"
    : STATUSES.includes(r.status as FaceLockStatus) ? (r.status as FaceLockStatus) : "training";
  const pictureUrl = typeof r.picture_url === "string" && r.picture_url ? r.picture_url : null;
  const sentState = PICTURE_STATES.includes(r.picture_state as FaceLockPictureState) ? (r.picture_state as FaceLockPictureState) : null;
  const choices = pictureUrl ? normalizeChoices(r.picture_choices) : [];
  return {
    id,
    name: str(r.name),
    picture_url: pictureUrl,
    picture_state: pictureUrl ? "ready" : (sentState && sentState !== "ready" ? sentState : "none"),
    ...(choices.length ? { picture_choices: choices } : {}),
    ...(pictureUrl && r.can_draw_choices === true ? { can_draw_choices: true } : {}),
    status,
    castable: r.castable === true,
    photo_count: num(r.photo_count, 0),
    message_key: typeof r.message_key === "string" && r.message_key ? r.message_key : null,
    progress: typeof r.progress === "number" && Number.isFinite(r.progress) ? r.progress : null,
    created_at: str(r.created_at),
    ...(r.needs_photos === true ? { needs_photos: true } : {}),
    ...(r.photos_ready === true ? { photos_ready: true } : {}),
    ...(r.adopting === true ? { adopting: true } : {}),
    ...(r.legacy === true ? { legacy: true } : {}),
    ...(r.photos_deleted === true ? { photos_deleted: true } : {}),
    ...(r.can_delete_photos === true ? { can_delete_photos: true } : {}),
    // STILLS FIRST: only when the server says so (an older one sends neither).
    ...(r.stills_ready === true ? { stills_ready: true } : {}),
    ...(r.video_ready === true ? { video_ready: true } : {}),
    ...(typeof r.video_eta_s === "number" && Number.isFinite(r.video_eta_s) && r.video_eta_s > 0 ? { video_eta_s: r.video_eta_s } : {}),
    ...(normalizeEnhance(r.enhance) ? { enhance: normalizeEnhance(r.enhance) as FaceLockEnhanceView } : {}),
    ...(normalizeAnimated(r.animated_picture) ? { animated_picture: normalizeAnimated(r.animated_picture) as FaceLockAnimatedPicture } : {}),
  };
}

export function normalizeCrew(raw: unknown): FaceLockCrew | null {
  if (!isRecord(raw)) return null;
  const e = isRecord(raw.eligibility) ? raw.eligibility : {};
  const max = Math.max(1, num(e.photo_max, FACELOCK_MAX_PHOTOS));
  const min = Math.min(max, Math.max(1, num(e.photo_min, FACELOCK_MIN_PHOTOS)));
  return {
    enabled: raw.enabled !== false,
    ...(typeof raw.picker === "boolean" ? { picker: raw.picker } : {}),
    ...(raw.required === true ? { required: true } : {}),
    ...(raw.admin === true ? { admin: true } : {}),
    eligibility: {
      signed_up: e.signed_up === true,
      purchased_credits: num(e.purchased_credits, 0),
      required_purchased: num(e.required_purchased, 0),
      can_create: e.can_create === true,
      next_cost: num(e.next_cost, 0),
      photo_min: min,
      photo_max: max,
      ...(e.gate_waived === true ? { gate_waived: true } : {}),
      ...(typeof e.busy === "boolean" ? { busy: e.busy } : {}),
      photo_extra_max: Math.max(0, Math.floor(num(e.photo_extra_max, 0))),
      ...(e.book_buyer === true ? { book_buyer: true } : {}),
      ...(typeof e.unlocked === "boolean" ? { unlocked: e.unlocked } : {}),
      ...(typeof e.balance === "number" && Number.isFinite(e.balance) ? { balance: e.balance } : {}),
      ...(e.flat_fee && typeof e.flat_fee === "object" && typeof (e.flat_fee as Record<string, unknown>).pack_type === "string"
        ? {
            flat_fee: {
              pack_type: String((e.flat_fee as Record<string, unknown>).pack_type),
              credits: num((e.flat_fee as Record<string, unknown>).credits, 0),
              price_cents: num((e.flat_fee as Record<string, unknown>).price_cents, 0),
              usd_cents: typeof (e.flat_fee as Record<string, unknown>).usd_cents === "number" ? Number((e.flat_fee as Record<string, unknown>).usd_cents) : null,
            },
          }
        : {}),
    },
    avatars: (Array.isArray(raw.avatars) ? raw.avatars : []).map(normalizeAvatar).filter((a): a is FaceLockAvatar => a !== null),
  };
}

const hasToken = (): boolean => {
  try { return !!localStorage.getItem("auth_token"); } catch { return false; }
};

/** GET /avatars. Off when signed out, when the route answers 404 (feature_off)
 *  or enabled:false; error for anything else that fails. */
export async function fetchFaceLockCrew(): Promise<FaceLockLoad> {
  if (!hasToken()) return { state: "off" };
  try {
    const r = await fetch(`${FACELOCK_API}/avatars`, { headers: adsAuthHeaders(false) });
    if (r.status === 404 || r.status === 401 || r.status === 403) return { state: "off" };
    if (!r.ok) return { state: "error", status: r.status };
    const crew = normalizeCrew(await r.json().catch(() => null));
    if (!crew) return { state: "error", status: r.status };
    if (!crew.enabled) return { state: "off" };
    return { state: "on", crew };
  } catch {
    return { state: "error", status: 0 };
  }
}

/** GET /open, no session: are avatars open to EVERY account (switched on, not
 *  admin-only)? False on any failure, so a closed or unknown answer never
 *  advertises the feature to a signed-out visitor. */
export async function fetchFaceLockOpen(): Promise<boolean> {
  try {
    const r = await fetch(`${FACELOCK_API}/open`);
    if (!r.ok) return false;
    const j: unknown = await r.json().catch(() => null);
    return isRecord(j) && j.open === true;
  } catch {
    return false;
  }
}

/** The avatar section on the account page (CastingCrew's id). */
export const AVATAR_SECTION_PATH = "/chopshop#casting-crew";
/** Signed out: sign up (or, from there, log in), then back to the avatar section. */
export const AVATAR_SIGNUP_PATH = `/signup?redirect_to=${encodeURIComponent(AVATAR_SECTION_PATH)}`;

async function send<T>(method: string, path: string, body: unknown, pick: (j: unknown) => T): Promise<FaceLockResult<T>> {
  try {
    const r = await fetch(`${FACELOCK_API}${path}`, {
      method,
      headers: adsAuthHeaders(body !== undefined),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const j: unknown = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = isRecord(j) ? j : {};
      return { ok: false, status: r.status, error: str(err.error) || `http_${r.status}`, message: str(err.message) };
    }
    return { ok: true, status: r.status, data: pick(j) };
  } catch (e: unknown) {
    return { ok: false, status: 0, error: "network", message: e instanceof Error ? e.message : String(e) };
  }
}

export function fetchFaceLockAvatar(id: string): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("GET", `/avatars/${encodeURIComponent(id)}`, undefined, normalizeAvatar);
}

/**
 * `source: "scan"` and `captions` (one training caption per photo, same order, no trigger word) come from FaceLock Scan.
 * A photo is its link, or, when the server takes labelled pictures (photo_extra_max > 0), its link with its label.
 */
export function createFaceLock(body: { name: string; photos: Array<string | (ScanPhotoLabel & { url: string })>; idempotency_key: string; source?: "scan"; captions?: string[] }): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", "/avatars", body, normalizeAvatar);
}

export function renameFaceLock(id: string, name: string): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("PATCH", `/avatars/${encodeURIComponent(id)}`, { name }, normalizeAvatar);
}

/** "Draw its picture again": free, the avatar's owner (or an admin). A portrait
 *  drawn with the avatar itself, never a photo. */
export function redrawFaceLockPicture(id: string): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/picture`, {}, normalizeAvatar);
}

/** "Draw new pictures to choose from": free, for a picture drawn before its choices were kept. */
export function drawFaceLockPictureChoices(id: string): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/picture`, { fresh: true }, normalizeAvatar);
}

/** The animated picture's "Draw it again" / "Draw a new one": new seeds, on the 3D worker (a new one costs credits). */
export function redrawFaceLockAnimated(id: string): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/picture-3d`, {}, normalizeAvatar);
}

/** "Choose its animated picture": which version the card shows. Free. */
export function chooseFaceLockAnimated(id: string, index: number): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/picture-3d/choose`, { index }, normalizeAvatar);
}

/** "Choose its picture": which of its pictures the card shows. Free. */
export function chooseFaceLockPicture(id: string, index: number): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/picture/choose`, { index }, normalizeAvatar);
}

/** Admin only, free: give an avatar that needs photos its photos. */
export function adoptFaceLock(id: string, body: { photos: string[] }): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/admin/avatars/${encodeURIComponent(id)}/adopt`, body, normalizeAvatar);
}

/** "Delete its photos": every photo file of the avatar is deleted. It keeps its picture but can't be cast after. */
export function deleteFaceLockPhotos(id: string): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("DELETE", `/avatars/${encodeURIComponent(id)}/photos`, undefined, normalizeAvatar);
}

/** A training photo: converted/resized to <=1600 px (`maxEdge`: an Ink Scan
 *  close-up keeps up to INK_MAX_EDGE), then POST /api/web/upload with type
 *  "facelock_photo". Returns the upload URL the routes accept. */
export async function uploadFaceLockPhoto(file: File, opts: { maxEdge?: number } = {}): Promise<string> {
  let token = "";
  try { token = localStorage.getItem("auth_token") || ""; } catch { token = ""; }
  const prepared = await preparePhotoForUpload(file, { maxEdge: opts.maxEdge ?? FACELOCK_MAX_EDGE });
  return uploadToWebStorage(prepared, { authToken: token, type: "facelock_photo" });
}

/** Upload every photo in order, reusing any already uploaded (a retry after a
 *  failed POST never uploads the same photo twice). Rejects with the file
 *  that failed, so the page can name it. */
export async function uploadFaceLockPhotos(
  files: File[],
  cache: Map<File, string>,
  onProgress?: (n: number, total: number) => void,
  maxEdgeOf?: (f: File) => number | undefined,
): Promise<string[]> {
  const urls: string[] = [];
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    onProgress?.(i + 1, files.length);
    let url = cache.get(f);
    if (!url) {
      try {
        url = await uploadFaceLockPhoto(f, { maxEdge: maxEdgeOf?.(f) });
      } catch (e: unknown) {
        throw new FaceLockUploadError(f.name, e instanceof Error ? e.message : String(e));
      }
      cache.set(f, url);
    }
    urls.push(url);
  }
  return urls;
}

export class FaceLockUploadError extends Error {
  fileName: string;
  reason: string;
  constructor(fileName: string, reason: string) {
    super(`${fileName}: ${reason}`);
    this.fileName = fileName;
    this.reason = reason;
  }
}

// ---- enhancements: FaceLock Ink Scan and FaceShot SharpShoot ----

/** An Ink Scan close-up keeps this much detail on upload (a scan picture: 1600). */
export const INK_MAX_EDGE = 2048;

/** One picture of an enhancement: a SharpShoot view (`bucket`, `caption`) or an Ink Scan view (`mark`, `view`). */
export type EnhancePhoto = { url: string; bucket: string; caption: string }
  | { url: string; mark: string; view: "wide" | "close_a" | "close_b"; box?: [number, number, number, number] };

export interface EnhanceStartBody {
  kinds: EnhanceKind[];
  /** The marks an Ink Scan names, in order: their ids are m1, m2... (a picture's `mark`). */
  marks: FaceLockMark[];
  parts: Array<"face" | "upper" | "full">;
  outfit: string;
  photos: EnhancePhoto[];
  idempotency_key: string;
}

export interface EnhanceRow {
  /** A scan view id (face_front...), a scene (scene_night, scene_day) or ink_<place>. */
  shot: string;
  /** For an ink row: the mark's place. */
  place: string | null;
  /** For a row of a part scanned before (ink_was_<section>): that part (marks.mjs MARK_SECTIONS). */
  section: MarkSection | null;
  /** The person's own picture of this view, when there is one. */
  real_url: string | null;
  base_url: string | null;
  new_url: string | null;
  base_broken: boolean;
  new_broken: boolean;
}

export interface FaceLockEnhanceDetail {
  id: string;
  state: EnhanceState | null;
  kinds: EnhanceKind[];
  marks: FaceLockMark[];
  outcome: string | null;
  rows: EnhanceRow[];
  portraits: { base: string[]; new: string[] };
  can_decide: boolean;
  can_rollback: boolean;
  decision_by: string | null;
  price: number;
}

const urlOrNull = (v: unknown): string | null => (typeof v === "string" && /^https?:\/\//i.test(v) ? v : null);

export function normalizeEnhanceDetail(raw: unknown): FaceLockEnhanceDetail | null {
  const r = isRecord(raw) && isRecord(raw.enhance) ? raw.enhance : null;
  if (!r || typeof r.id !== "string") return null;
  const ports = isRecord(r.portraits) ? r.portraits : {};
  const urls = (v: unknown) => (Array.isArray(v) ? v.map(urlOrNull).filter((u): u is string => !!u) : []);
  return {
    id: r.id,
    state: ENHANCE_STATES.includes(r.state as EnhanceState) ? (r.state as EnhanceState) : null,
    kinds: (Array.isArray(r.kinds) ? r.kinds : []).filter((k): k is EnhanceKind => k === "ink" || k === "sharpshoot"),
    marks: normalizeMarks(r.marks),
    outcome: typeof r.outcome === "string" && r.outcome ? r.outcome : null,
    rows: (Array.isArray(r.rows) ? r.rows : []).flatMap((x): EnhanceRow[] => (isRecord(x) && typeof x.shot === "string" ? [{
      shot: x.shot,
      place: typeof x.place === "string" ? x.place : null,
      section: MARK_SECTIONS.includes(x.section as MarkSection) ? (x.section as MarkSection) : null,
      real_url: urlOrNull(x.real_url),
      base_url: urlOrNull(x.base_url),
      new_url: urlOrNull(x.new_url),
      base_broken: x.base_broken === true,
      new_broken: x.new_broken === true,
    }] : [])),
    portraits: { base: urls(ports.base), new: urls(ports.new) },
    can_decide: r.can_decide === true,
    can_rollback: r.can_rollback === true,
    decision_by: typeof r.decision_by === "string" ? r.decision_by : null,
    price: Math.max(0, num(r.price, 0)),
  };
}

export function startFaceLockEnhance(id: string, body: EnhanceStartBody): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/enhance`, body, normalizeAvatar);
}

export function fetchFaceLockEnhance(id: string): Promise<FaceLockResult<FaceLockEnhanceDetail | null>> {
  return send("GET", `/avatars/${encodeURIComponent(id)}/enhance`, undefined, normalizeEnhanceDetail);
}

/** Keep the enhanced avatar (true) or the current one (false). */
export function decideFaceLockEnhance(id: string, eid: string, keep: boolean): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/enhance/${encodeURIComponent(eid)}/decide`, { keep }, normalizeAvatar);
}

/** "Go back to the previous version". Free. */
export function rollbackFaceLockEnhance(id: string): Promise<FaceLockResult<FaceLockAvatar | null>> {
  return send("POST", `/avatars/${encodeURIComponent(id)}/enhance/rollback`, {}, normalizeAvatar);
}

/** An enhancement is being learned or drawn on it right now. */
export const isEnhancing = (a: FaceLockAvatar): boolean => a.enhance?.running === true;

/** New picks added to a tray: unusable types and duplicates (same name and
 *  size) left out, capped at `max`. Says what was left out so the page can. */
export function mergePhotoPicks(prev: File[], picked: File[], max: number): { next: File[]; unsupported: File[]; dropped: number } {
  const unsupported = picked.filter((f) => !isAcceptedPhoto(f));
  const seen = new Set(prev.map((f) => `${f.name}_${f.size}`));
  const fresh: File[] = [];
  for (const f of picked) {
    const key = `${f.name}_${f.size}`;
    if (!isAcceptedPhoto(f) || seen.has(key)) continue;
    seen.add(key);
    fresh.push(f);
  }
  const merged = [...prev, ...fresh];
  return { next: merged.slice(0, max), unsupported, dropped: Math.max(0, merged.length - max) };
}

/** "free" or "50 credits". */
export function faceLockCostLabel(t: TFunction, cost: number): string {
  return cost > 0 ? t("faceLock.costCredits", { count: cost }) : t("faceLock.costFree");
}

/** One key per submit attempt: a double click or a retried request never
 *  makes (or charges) two FaceLocks. */
export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* fall through */ }
  return `fl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

// ---- derived values the UI shares ----

export const isTraining = (a: FaceLockAvatar): boolean =>
  a.status === "checking" || a.status === "training" || a.status === "finishing";

/** Something is running on it: training, or an admin's Add photos. */
export const isWorking = (a: FaceLockAvatar): boolean => isTraining(a) || a.adopting === true;

export const anyTraining = (avatars: FaceLockAvatar[]): boolean => avatars.some(isWorking);

/** Where the picture stands, from what the server sent (a picture_url is always a portrait). */
export function pictureStateOf(a: FaceLockAvatar): FaceLockPictureState {
  if (a.picture_url) return "ready";
  return a.picture_state && a.picture_state !== "ready" ? a.picture_state : "none";
}

/** Its picture is being made right now (learning the face, or drawing). */
export const isPictureInProgress = (a: FaceLockAvatar): boolean => {
  const s = pictureStateOf(a);
  return s === "learning" || s === "drawing";
};

/** Its animated picture is being drawn right now. */
export const isAnimatedInProgress = (a: FaceLockAvatar): boolean => a.animated_picture?.state === "drawing";

/** Anything that changes on its own: training, adding photos, or a picture being made. */
export const anyInProgress = (avatars: FaceLockAvatar[]): boolean => avatars.some((a) => isWorking(a) || isPictureInProgress(a) || isEnhancing(a));

/** "Draw its picture again": only once drawing it failed, and nothing else runs on it. */
export const canRedrawPicture = (a: FaceLockAvatar): boolean =>
  pictureStateOf(a) === "failed" && !isWorking(a) && a.status !== "failed";

export type FaceLockChip = "training" | "ready" | "failed" | "needsPhotos" | "adopting" | "stillsReady";

export function chipFor(a: FaceLockAvatar): FaceLockChip {
  if (a.status === "failed") return "failed";
  if (a.adopting) return "adopting";
  // STILLS FIRST: pictures work, its video files are still training.
  if (a.stills_ready === true && !a.castable) return "stillsReady";
  // Trained, but its photos are not on file yet: it cannot be used until they are.
  if (a.needs_photos && !a.castable && a.status === "ready") return "needsPhotos";
  if (a.status === "ready") return "ready";
  return "training";
}

/** "Choose its picture": it has a picture (its owner, or an admin, picks among its choices). */
export const canChoosePicture = (a: FaceLockAvatar): boolean => !!a.picture_url && a.status !== "failed";

/** "Delete its photos": the server says it has photo files, and nothing runs on it. */
export const canDeletePhotos = (a: FaceLockAvatar): boolean => a.can_delete_photos === true && !isWorking(a);

/** Signed up on the website and bought enough credits (an admin skips the purchase). */
export function isUnlocked(e: FaceLockEligibility): boolean {
  return e.signed_up && (e.unlocked === true || e.purchased_credits >= e.required_purchased || e.gate_waived === true);
}

// Where "Continue" on the checkout receipt (/success) goes after the flat-fee
// avatar: back to the page the buyer paid from. This tab only.
const CHECKOUT_RETURN_KEY = "checkout_return_v1";

/** Remember where to come back to after a checkout (a same-site path only). */
export function rememberCheckoutReturn(path: string, label: "avatar" | "storybook"): void {
  if (!path.startsWith("/") || path.startsWith("//")) return;
  try { sessionStorage.setItem(CHECKOUT_RETURN_KEY, JSON.stringify({ path, label, at: Date.now() })); } catch { /* no storage: the receipt shows its usual links */ }
}

/** Forget the remembered return (the receipt page, once it has read it). */
export function clearCheckoutReturn(): void {
  try { sessionStorage.removeItem(CHECKOUT_RETURN_KEY); } catch { /* nothing kept */ }
}

/** The remembered return, or null; a day old at most. */
export function takeCheckoutReturn(): { path: string; label: "avatar" | "storybook" } | null {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_RETURN_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as { path?: unknown; label?: unknown; at?: unknown };
    if (typeof r.path !== "string" || !r.path.startsWith("/") || r.path.startsWith("//")) return null;
    if (Date.now() - Number(r.at) > 24 * 60 * 60 * 1000) return null;
    return { path: r.path, label: r.label === "storybook" ? "storybook" : "avatar" };
  } catch {
    return null;
  }
}

/** Why "Create avatar" can't open the create dialog right now, or null when it can:
 *  "gate" (not signed up on the website, or not enough credits bought),
 *  "busy" (one of the account's avatars is being built), "closed" (the server
 *  says no for another reason). The button stays pressable in every case and
 *  says which, instead of being a dead greyed button. */
export type CreateAvatarBlock = "gate" | "pay" | "busy" | "closed";
export function createBlockOf(e: FaceLockEligibility, avatars: FaceLockAvatar[]): CreateAvatarBlock | null {
  if (!isUnlocked(e)) {
    // Signed up, not unlocked: the flat-fee avatar. Paid already (its credits on the balance): straight to the create.
    if (!e.signed_up || !e.flat_fee) return "gate";
    if ((e.balance ?? 0) < e.flat_fee.credits) return "pay";
  }
  if (e.can_create) return null;
  return e.busy === true || anyTraining(avatars) ? "busy" : "closed";
}

/** Add photos (admin only): an avatar that needs its photos, with nothing running on it. */
export function canAdopt(a: FaceLockAvatar, crew: Pick<FaceLockCrew, "admin"> | null): boolean {
  return crew?.admin === true && a.needs_photos === true && !a.castable && !isWorking(a) && a.status !== "failed";
}

/** The fewest photos Add photos takes for this avatar (the server has the final word). */
export const adoptMinPhotos = (a: FaceLockAvatar): number => (a.legacy ? FACELOCK_ADOPT_LEGACY_MIN_PHOTOS : FACELOCK_ADOPT_MIN_PHOTOS);

/** Is `count` photos one complete set the server takes (photo_min to photo_max, 16 to 24)? */
export const photoCountOk = (e: Pick<FaceLockEligibility, "photo_min" | "photo_max">, count: number): boolean =>
  count >= e.photo_min && count <= e.photo_max;

/** Castable avatars, newest first — what a picker offers. With `required` the
 *  picker is the only way in, whatever the picker switch says. */
export function castableAvatars(crew: FaceLockCrew | null): FaceLockAvatar[] {
  if (!crew || !crew.enabled || (crew.picker === false && crew.required !== true)) return [];
  return crew.avatars
    .filter((a) => a.castable)
    .sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
}

/** Avatars a kids storybook can draw a grown-up from (its face photos are checked), newest first. */
export function storybookAvatars(crew: FaceLockCrew | null): FaceLockAvatar[] {
  if (!crew || !crew.enabled) return [];
  return crew.avatars
    .filter((a) => a.photos_ready === true && !a.photos_deleted)
    .sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
}

// ---- a build waiting for its FaceLock (the never-lose record) ----
// With FACELOCK_CREW_REQUIRED a customer who has no FaceLock yet makes one
// right inside the corrido or ads flow, and it trains for a while. Their
// choices (song, genre, words; the whole ad) are kept here, per tool, so a
// reload or a return picks the build up with that FaceLock selected, and a
// page left open carries on by itself once it is ready.

export type FaceLockWaitTool = "corrido" | "ads";
/** Outlasts the 6 h training deadline with room to spare. */
export const FACELOCK_WAIT_TTL_MS = 24 * 60 * 60 * 1000;
export type FaceLockWait<T> = { avatarId: string; choices: T; savedAt: number };

const waitKey = (tool: FaceLockWaitTool) => `facelock:wait-${tool}-v1`;

export function saveFaceLockWait<T>(tool: FaceLockWaitTool, avatarId: string, choices: T): void {
  try {
    localStorage.setItem(waitKey(tool), JSON.stringify({ avatarId, choices, savedAt: Date.now() } satisfies FaceLockWait<T>));
  } catch { /* quota or private mode: the page still carries on while it is open */ }
}

export function readFaceLockWait<T>(tool: FaceLockWaitTool): FaceLockWait<T> | null {
  try {
    const raw = localStorage.getItem(waitKey(tool));
    if (!raw) return null;
    const rec = JSON.parse(raw) as FaceLockWait<T>;
    if (!rec || typeof rec.avatarId !== "string" || !rec.avatarId || typeof rec.savedAt !== "number") return null;
    if (Date.now() - rec.savedAt > FACELOCK_WAIT_TTL_MS) { clearFaceLockWait(tool); return null; }
    return rec;
  } catch {
    return null;
  }
}

export function clearFaceLockWait(tool: FaceLockWaitTool): void {
  try { localStorage.removeItem(waitKey(tool)); } catch { /* nothing to clear */ }
}

/** An error from Add photos, in plain words; the rest as any other route's. */
export function adoptErrorText(t: TFunction, res: { status: number; error: string; message: string }, min: number): string {
  if (res.error === "photo_count") return t("faceLock.adopt.photoCount", { min, max: FACELOCK_MAX_PHOTOS });
  if (res.error === "forbidden" || (res.status === 403 && res.error !== "photos_not_yours")) return t("faceLock.adopt.forbidden");
  if (res.error === "avatar_busy") return t("faceLock.adopt.busy");
  return faceLockErrorText(t, res);
}

/** An error from any /api/web/facelock route, in the customer's words. */
export function faceLockErrorText(t: TFunction, res: { status: number; error: string; message: string }): string {
  if (res.error === "insufficient_credits" || res.status === 402) return t("faceLock.errors.insufficient_credits");
  const known = ["avatar_busy", "avatar_needs_photos", "not_eligible", "feature_off", "photo_count", "photo_extra_count", "name_invalid", "photos_not_yours", "nothing_to_choose", "picture_unknown", "captions_invalid", "rate_limited", "network",
    "enhance_busy", "enhance_not_ready", "sharpshoot_incomplete", "ink_incomplete", "marks_invalid", "nothing_to_roll_back"];
  if (known.includes(res.error)) return t(`faceLock.errors.${res.error}`);
  if (res.status === 403) return t("faceLock.errors.not_eligible");
  if (res.message.trim()) return res.message;
  return t("faceLock.errors.generic");
}
